// client/src/features/sidebar/SidebarControls.jsx
import React, { useCallback, useMemo, useState } from 'react'
import { getSensorLabel, getFieldLabel } from '../../services/dataService'
import { CT2001_SENSOR_ORDER, SENSOR_PARAMETER_FIELDS } from '../../constants/sensors'
import styles from './SidebarControls.module.css'

// Directional fields that should be hidden from sidebar (now handled by chart button)
const DIRECTIONAL_FIELDS = [
  'dir_mean_deg',
  'wind_dir_avg_deg',
  'dir_deg'
];

const isDirectionalField = (field) =>
  DIRECTIONAL_FIELDS.some(dirField => field.includes(dirField))

const splitParametersAndRaw = (sensor, fields) => {
  const parameterSet = new Set(SENSOR_PARAMETER_FIELDS[sensor] || [])
  const visible = fields.filter(f => !isDirectionalField(f))
  const parameters = visible.filter(f => parameterSet.has(f))
  const raw = visible.filter(f => !parameterSet.has(f))
  return { parameters, raw }
}

const SidebarControls = React.memo(function SidebarControls({
  buoyId,
  availableSensors = [],
  availableFieldsBySensor = {},     // { [sensor]: string[] }
  selectedSensors = [],             // string[] for this specific buoy
  setSelectedSensors,
  selectedFields = [],              // string[] of "sensor:field" for this specific buoy
  setSelectedFields,
  isLoading = false                 // Add loading prop
}) {
  const fieldsForBuoy = selectedFields
  const [virtualSensorsExpanded, setVirtualSensorsExpanded] = useState(false)

  // Group sensors into virtual and CT2001 instruments. Order CT2001 sensors per spec.
  const { virtualSensors, ct2001Sensors } = useMemo(() => {
    const virtual = availableSensors.filter(s => s.startsWith('virtual_'))
    const regular = availableSensors.filter(s => !s.startsWith('virtual_'))
    const ordered = [
      ...CT2001_SENSOR_ORDER.filter(s => regular.includes(s)),
      ...regular.filter(s => !CT2001_SENSOR_ORDER.includes(s)),
    ]
    return { virtualSensors: virtual, ct2001Sensors: ordered }
  }, [availableSensors])

  // Sync selectedSensors with the set of sensors that have any field checked.
  const syncSensorSelection = useCallback((nextFields) => {
    const sensors = new Set()
    nextFields.forEach(fk => {
      const [sensor] = fk.split(':')
      if (sensor) sensors.add(sensor)
    })
    setSelectedSensors([...sensors])
  }, [setSelectedSensors])

  const toggleField = useCallback((fieldKey) => {
    const set = new Set(fieldsForBuoy)
    set.has(fieldKey) ? set.delete(fieldKey) : set.add(fieldKey)
    const next = [...set]
    setSelectedFields(next)
    syncSensorSelection(next)
  }, [fieldsForBuoy, setSelectedFields, syncSensorSelection])

  const toggleVirtualSensor = useCallback((sensor) => {
    const set = new Set(selectedSensors)
    if (set.has(sensor)) {
      set.delete(sensor)
      setSelectedFields(fieldsForBuoy.filter(fk => !fk.startsWith(`${sensor}:`)))
    } else {
      set.add(sensor)
    }
    setSelectedSensors([...set])
  }, [selectedSensors, fieldsForBuoy, setSelectedSensors, setSelectedFields])

  const renderFieldCheckboxes = (sensor, fields) =>
    fields.map(field => {
      const fk = `${sensor}:${field}`
      return (
        <label key={fk} className={styles.fieldCheckboxLabel}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={fieldsForBuoy.includes(fk)}
            onChange={() => toggleField(fk)}
          />
          {getFieldLabel(sensor, field)}
        </label>
      )
    })

  return (
    <div className={styles.sidebarContainer}>
      {/* CT2001 Sensors tree */}
      <div className={styles.sensorSection}>
        <div className={styles.sectionTitle}>
          {buoyId} Sensors
          {isLoading && <span className={styles.loadingIndicator}> (fetching...)</span>}
        </div>

        {ct2001Sensors.length === 0 && !isLoading && (
          <div className={styles.noSensorsText}>No sensors available</div>
        )}

        {ct2001Sensors.map(sensor => {
          const avail = availableFieldsBySensor[sensor] || []
          const { parameters, raw } = splitParametersAndRaw(sensor, avail)
          const sensorHasSelection = fieldsForBuoy.some(fk => fk.startsWith(`${sensor}:`))

          return (
            <details
              key={`${buoyId}-${sensor}`}
              className={styles.sensorTreeBranch}
              open={sensorHasSelection || undefined}
            >
              <summary className={styles.sensorTreeSummary}>
                {getSensorLabel(sensor)}
              </summary>

              {parameters.length > 0 && (
                <details className={styles.sensorTreeSubBranch} open>
                  <summary className={styles.sensorTreeSubSummary}>Parameters</summary>
                  {renderFieldCheckboxes(sensor, parameters)}
                </details>
              )}

              {raw.length > 0 && (
                <details className={styles.sensorTreeSubBranch}>
                  <summary className={styles.sensorTreeSubSummary}>Raw data</summary>
                  {renderFieldCheckboxes(sensor, raw)}
                </details>
              )}

              {parameters.length === 0 && raw.length === 0 && (
                <div className={styles.noSensorsText}>No fields available</div>
              )}
            </details>
          )
        })}
      </div>

      {/* Virtual Sensors (kept as a flat checkbox list - not part of CT2001 tree) */}
      {virtualSensors.length > 0 && (
        <div className={styles.sensorSection}>
          <details
            open={virtualSensorsExpanded}
            onToggle={(e) => setVirtualSensorsExpanded(e.target.open)}
            className={styles.detailsContainer}
          >
            <summary className={styles.detailsSummary}>
              Virtual Sensors
            </summary>
            {virtualSensors.map(sensor => (
              <label key={`${buoyId}-${sensor}`} className={styles.fieldCheckboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={selectedSensors.includes(sensor)}
                  onChange={() => toggleVirtualSensor(sensor)}
                />
                {getSensorLabel(sensor)}
              </label>
            ))}
          </details>
        </div>
      )}

      {/* Fields for selected virtual sensors (virtual sensors keep the legacy
          "Derived" / "Raw" split since they aren't part of the CT2001 tree). */}
      {selectedSensors
        .filter(s => s.startsWith('virtual_'))
        .map(sensor => {
          const avail = availableFieldsBySensor[sensor] || []
          const visible = avail.filter(f => !isDirectionalField(f))
          if (visible.length === 0) return null
          return (
            <div key={`${buoyId}-${sensor}-fields`} className={styles.fieldSection}>
              <div className={styles.fieldSectionTitle}>
                {getSensorLabel(sensor)} Fields
              </div>
              {renderFieldCheckboxes(sensor, visible)}
            </div>
          )
        })}
    </div>
  )
})

export default SidebarControls
